import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

interface RichTextEditorProps {
  value?: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: number;
  width?: string | number;
}

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Start writing...",
  height = 500,
  width = 750,
}: RichTextEditorProps) {
  const editorRef = React.useRef<HTMLDivElement>(null);
  const [isEditorFocused, setIsEditorFocused] = React.useState(false);

  React.useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleContentChange();
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle common keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case "b":
          e.preventDefault();
          handleCommand("bold");
          break;
        case "i":
          e.preventDefault();
          handleCommand("italic");
          break;
        case "u":
          e.preventDefault();
          handleCommand("underline");
          break;
        case "z":
          if (e.shiftKey) {
            e.preventDefault();
            handleCommand("redo");
          } else {
            e.preventDefault();
            handleCommand("undo");
          }
          break;
      }
    }
  };

  const formatButtons = [
    { command: "bold", icon: <FormatBoldIcon />, title: "Bold (Ctrl+B)" },
    { command: "italic", icon: <FormatItalicIcon />, title: "Italic (Ctrl+I)" },
    {
      command: "underline",
      icon: <FormatUnderlinedIcon />,
      title: "Underline (Ctrl+U)",
    },
  ];

  const listButtons = [
    {
      command: "insertUnorderedList",
      icon: <FormatListBulletedIcon />,
      title: "Bullet List",
    },
    {
      command: "insertOrderedList",
      icon: <FormatListNumberedIcon />,
      title: "Numbered List",
    },
  ];

  const alignButtons = [
    {
      command: "justifyLeft",
      icon: <FormatAlignLeftIcon />,
      title: "Align Left",
    },
    {
      command: "justifyCenter",
      icon: <FormatAlignCenterIcon />,
      title: "Align Center",
    },
    {
      command: "justifyRight",
      icon: <FormatAlignRightIcon />,
      title: "Align Right",
    },
  ];

  const historyButtons = [
    { command: "undo", icon: <UndoIcon />, title: "Undo (Ctrl+Z)" },
    { command: "redo", icon: <RedoIcon />, title: "Redo (Ctrl+Shift+Z)" },
  ];

  return (
    <Paper
      elevation={1}
      sx={{
        width: width,
        border: isEditorFocused ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      {/* Toolbar */}
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 48,
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {/* Format buttons */}
        {formatButtons.map((button, index) => (
          <IconButton
            key={index}
            size="small"
            onClick={() => handleCommand(button.command)}
            title={button.title}
            sx={{ mx: 0.25 }}
          >
            {button.icon}
          </IconButton>
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* List buttons */}
        {listButtons.map((button, index) => (
          <IconButton
            key={index}
            size="small"
            onClick={() => handleCommand(button.command)}
            title={button.title}
            sx={{ mx: 0.25 }}
          >
            {button.icon}
          </IconButton>
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Alignment buttons */}
        {alignButtons.map((button, index) => (
          <IconButton
            key={index}
            size="small"
            onClick={() => handleCommand(button.command)}
            title={button.title}
            sx={{ mx: 0.25 }}
          >
            {button.icon}
          </IconButton>
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* History buttons */}
        {historyButtons.map((button, index) => (
          <IconButton
            key={index}
            size="small"
            onClick={() => handleCommand(button.command)}
            title={button.title}
            sx={{ mx: 0.25 }}
          >
            {button.icon}
          </IconButton>
        ))}

        <Box sx={{ flexGrow: 1 }} />

        {/* Heading format dropdown */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ mr: 1 }}>
            Format:
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleCommand("formatBlock", "<h1>")}
            sx={{ minWidth: 40, fontSize: "0.75rem" }}
          >
            H1
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleCommand("formatBlock", "<h2>")}
            sx={{ minWidth: 40, fontSize: "0.75rem" }}
          >
            H2
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleCommand("formatBlock", "<h3>")}
            sx={{ minWidth: 40, fontSize: "0.75rem" }}
          >
            H3
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleCommand("formatBlock", "<p>")}
            sx={{ minWidth: 40, fontSize: "0.75rem" }}
          >
            P
          </Button>
        </Box>
      </Toolbar>

      {/* Editor Content */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleContentChange}
        onFocus={() => setIsEditorFocused(true)}
        onBlur={() => setIsEditorFocused(false)}
        onKeyDown={handleKeyDown}
        sx={{
          minHeight: height - 48, // Subtract toolbar height
          maxHeight: height - 48,
          overflow: "auto",
          padding: 2,
          fontSize: "14px",
          fontFamily: "Helvetica, Arial, sans-serif",
          lineHeight: 1.5,
          outline: "none",
          backgroundColor: "#fff",
          borderRadius: "0 0 4px 4px",
          "&:empty::before": {
            content: `"${placeholder}"`,
            color: "#aaa",
            fontStyle: "italic",
          },
          "& h1": {
            fontSize: "2rem",
            fontWeight: "bold",
            margin: "0.5rem 0",
          },
          "& h2": {
            fontSize: "1.5rem",
            fontWeight: "bold",
            margin: "0.5rem 0",
          },
          "& h3": {
            fontSize: "1.25rem",
            fontWeight: "bold",
            margin: "0.5rem 0",
          },
          "& p": {
            margin: "0.5rem 0",
          },
          "& ul, & ol": {
            margin: "0.5rem 0",
            paddingLeft: "2rem",
          },
          "& li": {
            margin: "0.25rem 0",
          },
        }}
      />
    </Paper>
  );
}
